import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from "@angular/core";
import isEqual from "lodash-es/isEqual";
import { Subscription } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";

import { FilterHatchService } from "@hermes/pages/grid-page";
import {
    GridFacade,
    GridFilterGroup,
    GridFilterTag,
} from "@hermes/states/grid-filters-state";
import { FocusService } from "@hermes/utils-generic/services/focus";

@Component({
    selector: "h-grid-filters-tags",
    templateUrl: "./grid-filters-tags.component.html",
    styleUrls: ["./grid-filters-tags.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridFiltersTagsComponent implements OnInit, OnDestroy {
    public gridFilterGroups: GridFilterGroup[] = [];

    // Both GridFilterGroup and GridFilter are needed in order to remove the correct filter.
    public selectedGridFilters: GridFilterTag[] = [];

    private subscription: Subscription = new Subscription();

    constructor(
        private gridFacade: GridFacade,
        private focusService: FocusService,
        private filterHatchService: FilterHatchService,
        private cd: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.gridFacade.filterGroups$
                .pipe(distinctUntilChanged(isEqual))
                .subscribe((gridFilterGroups: GridFilterGroup[]) => {
                    this.gridFilterGroups = gridFilterGroups;
                    this.populateSelectedGridFilters(gridFilterGroups);
                    this.cd.markForCheck();
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public removeFilter(gridFilter: GridFilterTag): void {
        this.handleFocus(gridFilter);
        gridFilter.filter.selected = false;
        if (gridFilter.priceRange) {
            this.gridFacade.clearPriceFilter();
        } else {
            this.gridFacade.updateGridFilter(
                gridFilter.filter,
                this.gridFilterGroups.find(
                    (gridFilterGroup) =>
                        gridFilterGroup.name === gridFilter.gridFilterGroupName,
                ),
            );
        }
    }

    public gridFilterId(_index: number, gridFilterTag: GridFilterTag): string {
        return gridFilterTag.id;
    }

    private populateSelectedGridFilters(
        gridFilterGroups: GridFilterGroup[],
    ): void {
        this.selectedGridFilters = [];
        gridFilterGroups.forEach((gridFilterGroup: GridFilterGroup) => {
            // Don't display switch filters
            if (this.isSwitchFilter(gridFilterGroup)) {
                return;
            }

            // Filter prices case - no filter insides is selected true and needs dedicated case.
            if (this.isFilterPriceSelected(gridFilterGroup)) {
                this.selectedGridFilters.push({
                    id: `${gridFilterGroup.presentation}-${gridFilterGroup.name}-price`,
                    filter: gridFilterGroup.filters[0],
                    gridFilterGroupName: gridFilterGroup.name,
                    priceRange: gridFilterGroup.priceRange,
                });
            } else {
                gridFilterGroup.filters.forEach((filter) => {
                    if (filter.selected) {
                        this.selectedGridFilters.push({
                            id: `${gridFilterGroup.presentation}-${gridFilterGroup.name}-${filter.name}`,
                            filter,
                            gridFilterGroupName: gridFilterGroup.name,
                            priceRange: gridFilterGroup.priceRange,
                        });
                    }
                });
            }
        });
    }

    private isSwitchFilter(gridFilterGroup: GridFilterGroup): boolean {
        return gridFilterGroup.presentation === "switch";
    }

    /**
     * All filters contained in GridFilterGroup does not contained isSelected info, we must check if priceRange changed.
     *
     * @param gridFilterGroup
     * @returns
     */
    private isFilterPriceSelected(gridFilterGroup: GridFilterGroup): boolean {
        return (
            gridFilterGroup.presentation === "slider" &&
            gridFilterGroup.priceRange &&
            (gridFilterGroup.priceRange.valueMax !==
                gridFilterGroup.priceRange.valueMaxCurrent ||
                gridFilterGroup.priceRange.valueMin !==
                    gridFilterGroup.priceRange.valueMinCurrent)
        );
    }

    private handleFocus(gridFilter: GridFilterTag): void {
        // If only one filter, it will be removed, then focus first toggle
        if (this.selectedGridFilters.length === 1) {
            this.filterHatchService.clearGridFilter();
            return;
        }
        // Otherwise, focus another filter tag
        let index = this.selectedGridFilters.findIndex(
            (selectedGridFilter) => selectedGridFilter.id === gridFilter.id,
        );
        // If last filter removed, focus previous filter (new last filter), if not focus next one.
        if (index === this.selectedGridFilters.length - 1) {
            index--;
        } else {
            index++;
        }
        this.focusService.elementToFocus = this.selectedGridFilters[index].id;
    }
}
